import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  adminResetPassword,
  adminSetAddons,
  adminSetTier,
  adminEnableOwner,
  fetchAdminTenantDetail,
  getCurrency,
  startImpersonation,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { parseServerDate } from "../utils/time";

/**
 * Founder drill-in page for a single tenant. Linked from the admin
 * table 'View' button. Shows everything you'd want to know before
 * picking up the phone to a hotel manager.
 *
 * Sections:
 *   - Header: name, status, ID, contact + actions (Login as / Set tier)
 *   - Health summary tiles
 *   - Recent orders (last 20)
 *   - Open tabs
 *   - Ratings with comments
 *   - Staff list
 *   - Menu + tables summary
 */

const TIER_LABELS = {
  free: "Trial",
  starter: "Starter",
  pro: "Professional",
  business: "Hotel",
  enterprise: "Enterprise",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return parseServerDate(iso).toLocaleString(); } catch { return "—"; }
}

function fmtRelativeTime(iso) {
  if (!iso) return "never";
  const ts = parseServerDate(iso).getTime();
  if (isNaN(ts)) return "never";
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}


// ----- Tier override modal -------------------------------------------
function TierOverrideModal({ current, onClose, onSave }) {
  const [tier, setTier] = useState(current || "free");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await onSave(tier, note);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not change tier.");
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, zIndex: 50,
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card"
        style={{ maxWidth: 520, width: "100%", padding: 24 }}
      >
        <h3 style={{ marginTop: 0 }}>Change subscription tier</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Manually overrides the tenant's tier without involving Stripe.
          Use this to grant pilot Business access during the 60-day
          pilot. If they later subscribe via Checkout, the webhook
          overwrites the manual tier.
        </p>
        <div
          style={{
            background: "#fff8e7",
            border: "1px solid #f0d38b",
            borderRadius: 8,
            padding: "10px 12px",
            margin: "8px 0 12px",
            fontSize: "0.85rem",
            color: "#8a5a00",
          }}
        >
          <b>⚠ Heads up:</b> This only changes the feature-flag tier
          on our side. It does <b>not</b> alter the tenant's Stripe
          subscription — they'll keep paying their current rate until
          they update their plan in the Stripe Customer Portal
          themselves. For paying customers wanting to change plans, ask
          them to use the "Manage billing" button on their Settings
          page instead.
        </div>

        <div className="field">
          <label>Tier</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="free">Trial / Free</option>
            <option value="starter">Starter (starter)</option>
            <option value="pro">Professional (pro)</option>
            <option value="business">Hotel (business)</option>
            <option value="enterprise">Enterprise (enterprise)</option>
          </select>
        </div>

        <div className="field">
          <label>Reason / note (audit log only)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. 60-day pilot until 14 May 2026"
          />
        </div>

        {err && <div className="error">{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Saving…" : "Apply"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ----- Sections ------------------------------------------------------
function HealthTiles({ health }) {
  const tiles = [
    { label: "Orders today", value: health.orders_today },
    { label: "Orders · 7d", value: health.orders_7d },
    { label: "Orders · 30d", value: health.orders_30d },
    { label: "Total orders", value: health.total_orders },
    { label: "Last order", value: fmtRelativeTime(health.last_order_at) },
    {
      label: "Food · 7d",
      value: health.avg_food_rating_7d != null ? `★ ${health.avg_food_rating_7d.toFixed(1)}` : "—",
      sub: `${health.rating_count_7d} reviews`,
    },
    {
      label: "Service · 7d",
      value: health.avg_service_rating_7d != null ? `★ ${health.avg_service_rating_7d.toFixed(1)}` : "—",
      sub: `${health.rating_count_7d} reviews`,
    },
    { label: "Open tabs", value: health.open_tabs_count },
  ];
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: 10, marginBottom: 24,
    }}>
      {tiles.map((t, i) => (
        <div key={i} className="card" style={{ padding: 12 }}>
          <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
            {t.label}
          </div>
          <div style={{ fontSize: "1.4rem", fontWeight: 700, marginTop: 2 }}>{t.value}</div>
          {t.sub && (
            <div className="muted" style={{ fontSize: "0.75rem", marginTop: 2 }}>{t.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
}


function OrdersSection({ orders }) {
  const currency = getCurrency();
  if (!orders.length) return <p className="muted">No orders yet.</p>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
      <thead>
        <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", color: "#5c7c7f", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
          <th style={{ padding: "6px 4px" }}>#</th>
          <th style={{ padding: "6px 4px" }}>Table</th>
          <th style={{ padding: "6px 4px" }}>Status</th>
          <th style={{ padding: "6px 4px" }}>Items</th>
          <th style={{ padding: "6px 4px", textAlign: "right" }}>Total</th>
          <th style={{ padding: "6px 4px" }}>Waiter</th>
          <th style={{ padding: "6px 4px" }}>When</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
            <td className="muted" style={{ padding: "6px 4px" }}>#{o.id}</td>
            <td style={{ padding: "6px 4px" }}>{o.table_number ?? "—"}</td>
            <td style={{ padding: "6px 4px" }}>{o.status}</td>
            <td style={{ padding: "6px 4px" }}>{o.item_count}</td>
            <td style={{ padding: "6px 4px", textAlign: "right", fontWeight: 600 }}>{formatPrice(o.total || 0, currency)}</td>
            <td className="muted" style={{ padding: "6px 4px" }}>{o.waiter_name || "—"}</td>
            <td className="muted" style={{ padding: "6px 4px" }}>{fmtRelativeTime(o.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function TabsSection({ tabs }) {
  const currency = getCurrency();
  if (!tabs.length) return <p className="muted">No open tabs.</p>;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {tabs.map((t) => (
        <div key={t.id} className="card" style={{ padding: 12, display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 600 }}>
              Table {t.table_number ?? "—"} · {t.round_count} round{t.round_count === 1 ? "" : "s"}
            </div>
            <div className="muted" style={{ fontSize: "0.78rem" }}>
              opened {fmtRelativeTime(t.opened_at)}
            </div>
          </div>
          <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#063B48" }}>
            {formatPrice(t.total || 0, currency)}
          </div>
        </div>
      ))}
    </div>
  );
}


function RatingsSection({ ratings }) {
  if (!ratings.length) return <p className="muted">No comments yet.</p>;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {ratings.map((r) => (
        <div key={r.id} className="card" style={{ padding: 12 }}>
          <div className="muted" style={{ fontSize: "0.78rem", display: "flex", gap: 12 }}>
            <span>Order #{r.order_id}</span>
            <span>★ food {r.food_rating}/5</span>
            <span>★ service {r.service_rating}/5</span>
            <span style={{ marginLeft: "auto" }}>{fmtRelativeTime(r.created_at)}</span>
          </div>
          {r.food_comment && (
            <p style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
              <b>Food:</b> {r.food_comment}
            </p>
          )}
          {r.service_comment && (
            <p style={{ margin: "4px 0 0", fontSize: "0.9rem" }}>
              <b>Service:</b> {r.service_comment}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}


function StaffSection({ staff }) {
  if (!staff.length) return <p className="muted">No staff accounts created yet — only the manager owner can log in.</p>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
      <thead>
        <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", color: "#5c7c7f", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
          <th style={{ padding: "6px 4px" }}>Name</th>
          <th style={{ padding: "6px 4px" }}>Email</th>
          <th style={{ padding: "6px 4px" }}>Role</th>
          <th style={{ padding: "6px 4px" }}>Active</th>
          <th style={{ padding: "6px 4px" }}>Joined</th>
        </tr>
      </thead>
      <tbody>
        {staff.map((s) => (
          <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
            <td style={{ padding: "6px 4px" }}>{s.name || <span className="muted">—</span>}</td>
            <td className="muted" style={{ padding: "6px 4px" }}>{s.email}</td>
            <td style={{ padding: "6px 4px" }}>{s.role}</td>
            <td style={{ padding: "6px 4px" }}>{s.active ? "✓" : "✗"}</td>
            <td className="muted" style={{ padding: "6px 4px" }}>{fmtRelativeTime(s.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


// ----- Reset password modal ------------------------------------------
function ResetPasswordModal({ tenantName, onClose, onReset }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (generate) => {
    setBusy(true); setErr(null);
    try {
      const res = await onReset(generate ? "" : pw);
      setResult(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reset password.");
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (result?.temporary_password && navigator.clipboard) {
      navigator.clipboard.writeText(result.temporary_password);
      setCopied(true);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 480, width: "100%", padding: 24 }}>
        <h3 style={{ marginTop: 0 }}>Reset login password</h3>
        {result ? (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              New password for <b>{tenantName}</b>. Copy it now and hand it over —
              it won't be shown again. Ask them to change it after they log in.
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "10px 0" }}>
              <code style={{ fontSize: "1.05rem", background: "#f2f4f5", padding: "8px 12px", borderRadius: 6, flex: 1, letterSpacing: 0.5 }}>
                {result.temporary_password}
              </code>
              <button type="button" className="btn secondary" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" className="btn" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              The old password is hashed and can't be recovered, so this sets a
              new one for <b>{tenantName}</b>. Type a temporary password, or
              generate one.
            </p>
            <div className="field">
              <label>New password (optional)</label>
              <input type="text" value={pw} onChange={(e) => { setPw(e.target.value); setErr(null); }}
                     placeholder="Leave blank to generate" />
            </div>
            {err && <div className="error">{err}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="btn secondary" onClick={() => submit(true)} disabled={busy}>Generate</button>
              <button type="button" className="btn" onClick={() => submit(false)} disabled={busy || pw.trim().length < 8}>Set password</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ----- Add-ons -------------------------------------------------------
// Readable labels for the à-la-carte add-on feature keys.
const ADDON_LABELS = {
  cost_control: "F&B Cost Control",
  group_orders: "Group & Event Orders",
  room_service: "Room Service",
  scheduling: "Staff Scheduling",
  stations: "Stations (kitchen / bar / café routing)",
  delivery: "Delivery Management (coming soon)",
  front_desk: "Front Desk / Rooms (reservations, check-in, housekeeping, invoicing)",
  guest_recognition: "Returning-guest recognition (Front Desk add-on)",
  shift_handover: "Shift handover — pass-on log (Front Desk add-on)",
  fd_upsell: "Front-desk upsell — upgrades & extras at check-in (Front Desk add-on)",
  online_checkin: "Online / pre-arrival check-in link (Front Desk add-on)",
  guest_messaging: "Guest messaging — Telegram check-in link & reminders (Front Desk add-on)",
  services: "Leisure & Wellness — spa / gym / pool / salon booking + billing (add-on)",
};

function AddonsCard({ profile, tenantId, onSaved }) {
  const catalog = profile.addon_catalog || [];
  const inTier = useMemo(() => new Set(profile.tier_features || []), [profile.tier_features]);
  const [sel, setSel] = useState(() => new Set(profile.feature_addons || []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState("");

  const toggle = (k) => {
    setSaved("");
    setSel((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await adminSetAddons(tenantId, Array.from(sel));
      setSaved("Saved — live for this venue.");
      await onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save add-ons.");
    } finally { setBusy(false); }
  };

  if (!catalog.length) return null;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Add-ons</h3>
      <p className="muted" style={{ marginTop: -4, maxWidth: 560 }}>
        Grant individual features on top of the tier. Takes effect immediately —
        the venue's dashboard unlocks the feature on next refresh.
      </p>
      {err && <div className="error" style={{ marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 8 }}>
        {catalog.map((k) => {
          const bundled = inTier.has(k);
          return (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.9rem", opacity: bundled ? 0.6 : 1 }}>
              <input
                type="checkbox"
                checked={bundled || sel.has(k)}
                disabled={bundled || busy}
                onChange={() => toggle(k)}
              />
              <span>
                {ADDON_LABELS[k] || k}
                {bundled && <span className="muted" style={{ fontSize: "0.75rem" }}> · included in tier</span>}
              </span>
            </label>
          );
        })}
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save add-ons"}
        </button>
        {saved && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: "0.85rem" }}>{saved}</span>}
      </div>
    </div>
  );
}


// ----- Main ----------------------------------------------------------
export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [ownerResult, setOwnerResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchAdminTenantDetail(id);
      setData(d); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load tenant.");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onSetTier = async (tier, note) => {
    await adminSetTier(id, tier, note);
    setShowTierModal(false);
    await load();
  };

  // Returns the API result ({ temporary_password, generated }) so the
  // modal can show the new password to copy. pw="" => server generates.
  const onResetPassword = (pw) => adminResetPassword(id, pw);

  // One-click: give this single hotel its own owner login + dashboard.
  const onEnableOwner = async () => {
    const email = window.prompt(
      "Owner login email (the owner uses this to sign in to their own dashboard, separate from the manager):",
      data?.profile?.email || ""
    );
    if (email === null) return; // cancelled
    setActionBusy(true);
    try {
      const res = await adminEnableOwner(id, email.trim() ? { email: email.trim() } : {});
      setOwnerResult(res);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not enable owner dashboard.");
    } finally { setActionBusy(false); }
  };

  const onLoginAs = async () => {
    if (!window.confirm(
      `Login as ${data.profile.name}? Your admin session will be saved and you can exit back to it at any time.`
    )) return;
    setActionBusy(true);
    try {
      await startImpersonation(id);
      // Hard reload to root so the app picks up the new session.
      window.location.href = "/";
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start impersonation.");
      setActionBusy(false);
    }
  };

  if (loading && !data) {
    return <div style={{ padding: 24 }}>Loading tenant…</div>;
  }
  if (err) {
    return (
      <div style={{ padding: 24 }}>
        <div className="error">{err}</div>
        <Link to="/admin" className="btn secondary" style={{ marginTop: 12 }}>← Back to admin</Link>
      </div>
    );
  }
  if (!data) return null;

  const { profile, health, recent_orders, open_tabs,
          recent_ratings_with_comments, staff, menu, tables_count } = data;

  return (
    <div style={{ padding: 0 }}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/admin" style={{ fontSize: "0.85rem", color: "#5c7c7f", textDecoration: "none" }}>
          ← Back to Founder HQ
        </Link>
      </div>

      {/* Header */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ margin: 0 }}>{profile.name}</h1>
            <div className="muted" style={{ marginTop: 4 }}>
              #{profile.id} · {profile.email} · {profile.country}
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span className={`tier-badge tier-${profile.subscription_tier}`}>
                {TIER_LABELS[profile.subscription_tier] || profile.subscription_tier}
              </span>
              {profile.has_subscription && (
                <span className="muted" style={{ fontSize: "0.78rem" }}>
                  Stripe ✓ {profile.stripe_subscription_id}
                </span>
              )}
              {profile.created_at && (
                <span className="muted" style={{ fontSize: "0.78rem" }}>
                  joined {fmtTime(profile.created_at)}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn secondary" onClick={() => setShowTierModal(true)} disabled={actionBusy}>
              Change tier
            </button>
            <button className="btn secondary" onClick={() => setShowResetModal(true)} disabled={actionBusy}>
              Reset password
            </button>
            <button className="btn secondary" onClick={onEnableOwner} disabled={actionBusy} title="Give this hotel its own owner login + dashboard">
              Owner login
            </button>
            {/* Per-tenant Settings page — country, languages, branding.
                This is the admin-only home for what used to live on
                ManagerBrandingPage. */}
            <Link
              to={`/admin/tenants/${id}/settings`}
              className="btn secondary"
              style={{ textDecoration: "none" }}
            >
              Edit settings
            </Link>
            <button className="btn" onClick={onLoginAs} disabled={actionBusy}>
              {actionBusy ? "Switching…" : "Login as this tenant"}
            </button>
          </div>
        </div>
      </div>

      {ownerResult && (
        <div className="card" style={{ padding: 14, marginBottom: 16,
          background: ownerResult.created ? "#e7f6ec" : "#fff8ec",
          border: "1px solid " + (ownerResult.created ? "#b7e0c4" : "#f0d38b") }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {ownerResult.created ? "✓ Owner dashboard enabled" : "Owner dashboard already exists"}
          </div>
          <div style={{ fontSize: "0.9rem" }}>{ownerResult.message}</div>
          {ownerResult.owner_email && (
            <div style={{ marginTop: 8 }}>Owner login email: <b>{ownerResult.owner_email}</b></div>
          )}
          {ownerResult.owner_password && (
            <div>Temporary password: <b style={{ fontFamily: "monospace" }}>{ownerResult.owner_password}</b>{" "}
              <span className="muted" style={{ fontSize: "0.8rem" }}>(shown once — copy it now)</span>
            </div>
          )}
          <button className="btn secondary small" style={{ marginTop: 8 }} onClick={() => setOwnerResult(null)}>Dismiss</button>
        </div>
      )}

      {/* Health tiles */}
      <HealthTiles health={health} />

      {/* Orders */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Recent orders</h3>
        <OrdersSection orders={recent_orders} />
      </div>

      {/* Open tabs */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Open tabs</h3>
        <TabsSection tabs={open_tabs} />
      </div>

      {/* Recent ratings */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Recent guest comments</h3>
        <RatingsSection ratings={recent_ratings_with_comments} />
      </div>

      {/* Staff */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Staff accounts ({staff.length})</h3>
        <StaffSection staff={staff} />
      </div>

      {/* Setup summary */}
      <div className="card" style={{ padding: 16, marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
        <div>
          <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Menu items</div>
          <div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{menu.total}</div>
          <div className="muted" style={{ fontSize: "0.78rem" }}>{menu.available} available</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Categories</div>
          <div style={{ fontSize: "0.9rem" }}>{menu.categories.length ? menu.categories.join(", ") : <span className="muted">—</span>}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Tables / service areas</div>
          <div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{tables_count}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Feature flags</div>
          <div style={{ fontSize: "0.85rem" }}>
            {profile.tier_features.length
              ? profile.tier_features.join(", ")
              : <span className="muted">none</span>}
          </div>
        </div>
      </div>

      {/* Add-ons — grant à-la-carte features on top of the tier */}
      <AddonsCard profile={profile} tenantId={id} onSaved={load} />

      {showTierModal && (
        <TierOverrideModal
          current={profile.subscription_tier}
          onClose={() => setShowTierModal(false)}
          onSave={onSetTier}
        />
      )}

      {showResetModal && (
        <ResetPasswordModal
          tenantName={profile.name}
          onClose={() => setShowResetModal(false)}
          onReset={onResetPassword}
        />
      )}
    </div>
  );
}
